import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth';
import { UserAvatar } from './components/UserAvatar';
import { LanguageSelectorCircle } from './components/LanguageSelectorCircle';
import { UrbanNetworkLoaderV2 } from './components';
import './admin-layout.css';

/**
 * AdminTopBar - Barra superior para SuperAdmin Dashboard
 *
 * @version 1.0
 * @module core/layouts
 *
 * @description
 * Barra de navegación horizontal fija para el dashboard de SuperAdmin.
 * Sin glassmorphism - usa background sólido.
 * Completamente independiente del TopBar del MapLayout.
 *
 * @remarks
 * **Estructura Grid (3 columnas)**:
 * - Izquierda: Espaciador vacío (para centrar logo)
 * - Centro: Logo WESTEER (link a /admin/dashboard)
 * - Derecha: UserAvatar con dropdown menu
 *
 * **Diseño**:
 * - Fixed position con z-index: var(--z-navbar)
 * - Background sólido (var(--bg-primary)) - SIN glassmorphism
 * - Height: 60px
 * - Grid layout: 1fr auto 1fr
 *
 * @example
 * ```tsx
 * import { AdminTopBar } from '@/core/layouts';
 *
 * function SuperAdminLayout() {
 *   return (
 *     <div>
 *       <AdminTopBar />
 *       <main>{children}</main>
 *     </div>
 *   );
 * }
 * ```
 */
export function AdminTopBar(): JSX.Element {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  /**
   * Maneja el evento de logout
   */
  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  return (
    <header className="admin-topbar" role="banner" aria-label="Barra de navegación de administración">
      {/* Espaciador izquierdo (para centrar logo) */}
      <div className="admin-topbar-spacer" />

      {/* Logo WESTEER centrado */}
      <Link to="/admin/overview" className="admin-topbar-logo" aria-label="Ir al resumen general de administración">
        <UrbanNetworkLoaderV2 size="xs" />
        <span className="admin-topbar-wordmark">WESTEER</span>
      </Link>

      {/* Language selector y User avatar (derecha) */}
      <div className="admin-topbar-actions">
        <LanguageSelectorCircle />
        {user && <UserAvatar user={user} onLogout={handleLogout} />}
      </div>
    </header>
  );
}
